import { useAuth } from '@/contexts/AuthContext';

/**
 * Resolve a lista de clubIds em escopo, respeitando o `viewScope` ativo
 * (selecionado via NetworkScopeSwitcher).
 *
 * Regras:
 *  - `viewScope.type === 'network'` → todos clubes da rede (`networkClubIds`)
 *    com fallback para `[club.id]` quando a lista estiver vazia (carregamento
 *    inicial ou usuário sem rede vinculada).
 *  - `viewScope.type === 'club'` → o clube selecionado pelo switcher
 *    (`viewScope.clubId`), NÃO o `club.id` do AuthContext (que é o clube
 *    principal do usuário e não muda quando o switcher troca de clube).
 *  - Sem `club` carregado → `[]`.
 *
 * Bug raiz corrigido: PRs anteriores (#188 / #192) usavam `[club.id]` no
 * branch `'club'`, ignorando completamente a troca de clube no switcher
 * para usuários multi-clube. As queries continuavam puxando dados do clube
 * principal mesmo após selecionar outro clube na barra.
 */
export function useScopedClubIds(): {
  isNetwork: boolean;
  scopedClubIds: string[];
} {
  const { club, viewScope, networkClubIds } = useAuth();
  const isNetwork = viewScope.type === 'network';

  if (isNetwork) {
    const ids = networkClubIds.length > 0 ? networkClubIds : club ? [club.id] : [];
    return { isNetwork: true, scopedClubIds: ids };
  }

  // viewScope.type === 'club' — usar SEMPRE o clubId do switcher, não club.id.
  if (viewScope.type === 'club' && viewScope.clubId) {
    return { isNetwork: false, scopedClubIds: [viewScope.clubId] };
  }

  return { isNetwork: false, scopedClubIds: club ? [club.id] : [] };
}
